import {
  Badge,
  Box,
  Collapse,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { type DocumentNode, print } from 'graphql';
import React, { useCallback } from 'react';

import { getHealthCheckId, isRestHealthCheck } from '@revelio/data-access';
import { FormatType, LineChart } from '@revelio/replots';

import { environment } from '../../../environments/environment';
import type { HealthCheckDetailsProps } from '../types';
import {
  CodeDisplay,
  HealthCheckHeader,
  QueryPlayground,
} from './health-check-details';
import { ServiceTimeline } from './ServiceTimeline';

// Display-only mirror of dash-meta's `resolveRestUrl`: substitute
// `{ENV_VAR}` placeholders from the dashboard's own environment (the same
// hosts per env) so admins can see the URL actually probed. Falls back to
// the raw placeholder when the frontend config doesn't carry the variable.
const resolveDisplayUrl = (url: string): string =>
  url.replace(/\{([A-Z_][A-Z0-9_]*)\}/g, (match, key: string) => {
    const value = (environment as unknown as Record<string, unknown>)[key];
    return typeof value === 'string' && value.length > 0 ? value : match;
  });

export const HealthCheckDetails: React.FC<HealthCheckDetailsProps> = ({
  check,
  latestResult,
  serviceResults,
  onRunSingleCheck,
  isFetching,
}) => {
  const [showSchema, setShowSchema] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<number>(0);
  // REST checks have no `variables` (they're just /ping probes); fall back
  // to an empty object so the playground state stays well-typed.
  const [currentVariables, setCurrentVariables] = React.useState<
    Record<string, unknown>
  >(check.variables ?? {});
  const {
    isOpen: isChartOpen,
    onOpen: onChartOpen,
    onClose: onChartClose,
  } = useDisclosure();

  const handleVariablesChange = useCallback(
    (variables: Record<string, unknown>) => {
      setCurrentVariables(variables);
    },
    []
  );

  const handleRunCheck = () => {
    setShowSchema(true);
    onRunSingleCheck(
      getHealthCheckId(check.service, check.viewGroup, check.view),
      currentVariables
    );
    setActiveTab(0);
  };

  // REST checks (the upstream mappers) don't ship a GraphQL document, so
  // the playground / query / schema tabs don't apply — render a slim
  // endpoint panel instead and leave the timeline + chart shared.
  const isRest = isRestHealthCheck(check);

  // Get the query document as a string (GraphQL only — guarded by isRest below)
  const queryString = isRest ? '' : print(check.query as DocumentNode);

  return (
    <Box
      key={getHealthCheckId(check.service, check.viewGroup, check.view)}
      p={4}
      borderWidth="1px"
      borderRadius="md"
      bg="white"
      _hover={{ borderColor: 'blue.200' }}
      transition="all 0.2s"
    >
      <VStack align="stretch" spacing={4}>
        <HealthCheckHeader
          service={check.service}
          viewGroup={check.viewGroup}
          view={check.view}
          status={latestResult?.status || 'unknown'}
          showSchema={showSchema}
          onToggleSchema={() => setShowSchema(!showSchema)}
          onChartOpen={onChartOpen}
          onRunCheck={handleRunCheck}
          isFetching={isFetching}
        />

        {latestResult?.error && (
          <Text color="red.500">Error: {latestResult.error.message}</Text>
        )}

        <Collapse in={showSchema} animateOpacity>
          {isRestHealthCheck(check) ? (
            <VStack align="stretch" spacing={2}>
              <Box>
                <Badge colorScheme="purple" mr={2}>
                  REST
                </Badge>
                <Text as="span" fontSize="sm" color="gray.600">
                  {check.request.body ? 'POST' : 'GET'}{' '}
                  {resolveDisplayUrl(check.request.url)}
                </Text>
              </Box>
              {check.request.body && (
                <CodeDisplay
                  code={JSON.stringify(check.request.body, null, 2)}
                />
              )}
              {/* The runner reports the URL it actually probed (post
                  placeholder-resolution) in error.details — ground truth
                  even when the frontend env can't resolve the placeholder
                  (local serve keeps API roots empty). */}
              {latestResult?.error?.details != null && (
                <CodeDisplay
                  code={JSON.stringify(latestResult.error.details, null, 2)}
                />
              )}
              {latestResult?.data && (
                <CodeDisplay
                  code={JSON.stringify(latestResult.data, null, 2)}
                />
              )}
            </VStack>
          ) : (
            <VStack align="stretch" spacing={4}>
              <Tabs
                variant="enclosed"
                size="sm"
                index={activeTab}
                onChange={setActiveTab}
              >
                <TabList>
                  <Tab>Playground</Tab>
                  <Tab>Query</Tab>
                  <Tab>Schema</Tab>
                </TabList>

                <TabPanels>
                  <TabPanel>
                    <QueryPlayground
                      check={check}
                      latestResult={latestResult}
                      onRunCheck={handleVariablesChange}
                    />
                  </TabPanel>

                  <TabPanel>
                    {activeTab === 1 && <CodeDisplay code={queryString} />}
                  </TabPanel>

                  <TabPanel>
                    {activeTab === 2 && (
                      <CodeDisplay
                        code={JSON.stringify(check.query, null, 2)}
                      />
                    )}
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </VStack>
          )}
        </Collapse>

        <Box>
          <ServiceTimeline serviceResults={serviceResults} check={check} />
        </Box>

        <Modal isOpen={isChartOpen} onClose={onChartClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Response Time History</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <Box height="400px">
                <LineChart
                  data={[
                    {
                      label: 'Response Time',
                      values: serviceResults
                        .filter((result) => result.status === 'success')
                        .map((result) => ({
                          date: result.timestamp,
                          value: result.responseTime,
                        }))
                        .sort(
                          (a, b) =>
                            new Date(a.date).getTime() -
                            new Date(b.date).getTime()
                        ),
                      color: '#5BD992',
                    },
                  ]}
                  format={FormatType.INTEGER}
                  dateFormat="daytime"
                  showGridLines={true}
                />
              </Box>
              <Text fontSize="sm" color="gray.500" mt={4}>
                Shows response times for successful health checks only. Lower is
                better.
              </Text>
            </ModalBody>
          </ModalContent>
        </Modal>

        {showSchema && (
          <>
            <Text fontSize="sm" color="gray.500">
              Last Response Time:{' '}
              {latestResult
                ? `${latestResult.responseTime.toFixed(0)}ms${
                    latestResult.responseTime < 100
                      ? ' (potentially cached)'
                      : ''
                  }`
                : 'N/A'}
            </Text>
            {serviceResults.length > 0 && (
              <Text fontSize="sm" color="gray.500">
                Avg Successful Response Time: {(() => {
                  const successfulChecks = serviceResults.filter(
                    (r) =>
                      r.status === 'success' &&
                      !r.isManualCheck &&
                      r.service === check.service &&
                      r.viewGroup === check.viewGroup &&
                      r.view === check.view
                  );
                  if (successfulChecks.length === 0) return 'N/A';
                  const avgTime =
                    successfulChecks.reduce(
                      (sum, r) => sum + r.responseTime,
                      0
                    ) / successfulChecks.length;
                  return `${avgTime.toFixed(0)}ms`;
                })()}
              </Text>
            )}
          </>
        )}
      </VStack>
    </Box>
  );
};
